import { useState } from "react";
import { Modal, DatePicker, Select } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { rescheduleDemo } from "../services/demoService";
import { DEMO_TIME_SLOTS, demoDay, formatDemoWhen } from "../utils/demo";

/**
 * Move a booked demo to another slot.
 *
 * The backend edits the existing Teams meeting rather than creating a new one,
 * so the join link survives, and it emails the lead and the rep itself.
 */
const RescheduleDemoModal = ({ lead, onClose, onRescheduled }) => {
  const queryClient = useQueryClient();
  const [date, setDate] = useState(() => {
    const d = demoDay(lead);
    return d?.isValid() ? d : null;
  });
  const [time, setTime] = useState(lead?.demo_time || null);
  const [errors, setErrors] = useState({});

  const mutation = useMutation({
    mutationFn: (payload) => rescheduleDemo(lead.id, payload),
    onSuccess: (updated) => {
      toast.success("Demo rescheduled — lead & rep notified by email.");
      /* Every lead surface reads the demo fields, so they all go stale at once. */
      ["inbox-leads", "enquiryList", "unassigned-leads", "future-leads", "lead-details"].forEach(
        (key) => queryClient.invalidateQueries({ queryKey: [key], refetchType: "all" }),
      );
      if (typeof onRescheduled === "function") onRescheduled(updated);
      onClose();
    },
    onError: (err) => toast.error(err?.message || "Could not reschedule this demo"),
  });

  const handleSubmit = () => {
    const next = {};
    if (!date) next.date = "Pick a date for the demo";
    if (!time) next.time = "Pick a time slot";
    setErrors(next);
    if (Object.keys(next).length) return;
    mutation.mutate({ demo_date: date.format("YYYY-MM-DD"), demo_time: time });
  };

  const label = { fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 };
  const errStyle = { color: "#dc2626", fontSize: 12, marginTop: 5 };

  return (
    <Modal
      open
      title={<span style={{ fontWeight: 700 }}>Reschedule demo</span>}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={mutation.isPending ? "Rescheduling…" : "Reschedule"}
      okButtonProps={{ loading: mutation.isPending }}
      cancelButtonProps={{ disabled: mutation.isPending }}
      destroyOnClose
      width={440}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 4 }}>
        <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{lead?.name || "—"}</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
            Currently {formatDemoWhen(lead)}
          </div>
        </div>

        <div>
          <label style={label}>New date</label>
          <DatePicker
            value={date}
            onChange={(v) => { setDate(v); setErrors((p) => ({ ...p, date: "" })); }}
            format="DD-MM-YYYY"
            style={{ width: "100%" }}
            // A demo cannot be moved into the past.
            disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"), "day")}
          />
          {errors.date && <div style={errStyle}>{errors.date}</div>}
        </div>

        <div>
          <label style={label}>New time (IST)</label>
          <Select
            value={time}
            onChange={(v) => { setTime(v); setErrors((p) => ({ ...p, time: "" })); }}
            placeholder="Select a slot"
            style={{ width: "100%" }}
            options={DEMO_TIME_SLOTS.map((s) => ({ value: s, label: s }))}
          />
          {errors.time && <div style={errStyle}>{errors.time}</div>}
        </div>

        <div style={{ fontSize: 11.5, color: "#6b7280", lineHeight: 1.5 }}>
          The same Teams meeting is moved, so the existing join link keeps working.
          Both the lead and the assigned rep are emailed automatically.
        </div>
      </div>
    </Modal>
  );
};

export default RescheduleDemoModal;
